'use client'

import * as React from 'react'
import { Button } from '@/components/ui/button'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@/components/ui/input-group'
import { cn } from '@/lib/utils'
import { Combobox as ComboboxPrimitive } from '@base-ui/react'
import { CheckIcon, ChevronDownIcon, XIcon } from 'lucide-react'

const Combobox = ComboboxPrimitive.Root
export type ComboboxSize = 'default' | 'sm' | 'md' | 'middle' | 'lg' | 'large'
type ResolvedComboboxSize = 'default' | 'lg'

const resolveComboboxSize = (size?: ComboboxSize): ResolvedComboboxSize =>
  size === 'lg' || size === 'large' ? 'lg' : 'default'

function ComboboxValue({ ...props }: ComboboxPrimitive.Value.Props) {
  return <ComboboxPrimitive.Value data-slot='combobox-value' {...props} />
}

const ComboboxTrigger = React.forwardRef<HTMLButtonElement, ComboboxPrimitive.Trigger.Props>(
  ({ className, children, ...props }, ref) => {
    return (
      <ComboboxPrimitive.Trigger
        ref={ref}
        data-slot='combobox-trigger'
        className={cn("[&_svg:not([class*='size-'])]:size-4", className)}
        {...props}
      >
        {children}
        <ChevronDownIcon className='pointer-events-none size-4 text-muted-foreground' />
      </ComboboxPrimitive.Trigger>
    )
  }
)

function ComboboxClear({
  className,
  size = 'default',
  ...props
}: ComboboxPrimitive.Clear.Props & {
  size?: ComboboxSize
}) {
  const resolvedSize = resolveComboboxSize(size)

  return (
    <ComboboxPrimitive.Clear
      data-slot='combobox-clear'
      render={
        <InputGroupButton variant='ghost' size={resolvedSize === 'lg' ? 'icon-sm' : 'icon-xs'} />
      }
      className={cn(className)}
      {...props}
    >
      <XIcon className='pointer-events-none' />
    </ComboboxPrimitive.Clear>
  )
}

function ComboboxInput({
  className,
  children,
  disabled = false,
  readOnly = false,
  inputReadOnly = readOnly,
  showTrigger = true,
  showClear = false,
  inputGroupRef,
  size = 'default',
  ...props
}: Omit<ComboboxPrimitive.Input.Props, 'size'> & {
  inputReadOnly?: boolean
  showTrigger?: boolean
  showClear?: boolean
  inputGroupRef?: React.Ref<HTMLDivElement>
  size?: ComboboxSize
}) {
  const resolvedSize = resolveComboboxSize(size)
  const showPointerCursor = !disabled && !readOnly && inputReadOnly

  return (
    <InputGroup
      ref={inputGroupRef}
      disabled={disabled}
      readOnly={readOnly}
      data-size={resolvedSize}
      className={cn(
        'w-full data-[size=default]:h-7 data-[size=lg]:h-9',
        showPointerCursor && 'cursor-pointer',
        className
      )}
    >
      <ComboboxPrimitive.Input
        render={
          <InputGroupInput
            disabled={disabled}
            readOnly={inputReadOnly}
            className={cn('h-full', showPointerCursor && 'cursor-pointer')}
          />
        }
        {...props}
      />
      <InputGroupAddon align='inline-end' className={cn(showPointerCursor && 'cursor-pointer')}>
        {showTrigger && (
          <InputGroupButton
            size={resolvedSize === 'lg' ? 'icon-sm' : 'icon-xs'}
            variant='ghost'
            render={<ComboboxTrigger />}
            data-slot='input-group-button'
            className='group-has-data-[slot=combobox-clear]/input-group:hidden data-pressed:bg-transparent'
            disabled={disabled}
          />
        )}
        {showClear && <ComboboxClear size={resolvedSize} disabled={disabled} />}
      </InputGroupAddon>
      {children}
    </InputGroup>
  )
}

function ComboboxContent({
  className,
  side = 'bottom',
  sideOffset = 6,
  align = 'start',
  alignOffset = 0,
  anchor,
  ...props
}: ComboboxPrimitive.Popup.Props &
  Pick<
    ComboboxPrimitive.Positioner.Props,
    'side' | 'align' | 'sideOffset' | 'alignOffset' | 'anchor'
  >) {
  return (
    <ComboboxPrimitive.Portal>
      <ComboboxPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        anchor={anchor}
        className='isolate z-2100'
      >
        <ComboboxPrimitive.Popup
          data-slot='combobox-content'
          data-chips={!!anchor}
          className={cn(
            'group/combobox-content relative max-h-(--available-height) w-(--anchor-width) max-w-(--available-width) min-w-[calc(var(--anchor-width)+--spacing(7))] origin-(--transform-origin) overflow-hidden rounded-md bg-popover text-popover-foreground shadow-md ring-1 ring-foreground/10 duration-100 data-[chips=true]:min-w-(--anchor-width) data-[side=bottom]:slide-in-from-top-2 data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 *:data-[slot=input-group]:m-1 *:data-[slot=input-group]:mb-0 *:data-[slot=input-group]:h-8 *:data-[slot=input-group]:border-input/30 *:data-[slot=input-group]:bg-input/30 *:data-[slot=input-group]:shadow-none data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
            className
          )}
          {...props}
        />
      </ComboboxPrimitive.Positioner>
    </ComboboxPrimitive.Portal>
  )
}

const ComboboxList = React.forwardRef<HTMLDivElement, ComboboxPrimitive.List.Props>(
  ({ className, ...props }, ref) => {
    return (
      <ComboboxPrimitive.List
        ref={ref}
        data-slot='combobox-list'
        className={cn(
          'clean-scrollbar max-h-[min(calc(--spacing(72)---spacing(9)),calc(var(--available-height)---spacing(9)))] scroll-py-1 overflow-y-auto overscroll-contain p-1 data-empty:p-0',
          className
        )}
        {...props}
      />
    )
  }
)

function ComboboxItem({ className, children, ...props }: ComboboxPrimitive.Item.Props) {
  return (
    <ComboboxPrimitive.Item
      data-slot='combobox-item'
      className={cn(
        "relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pl-2 pr-8 text-sm outline-hidden select-none data-highlighted:bg-accent data-highlighted:text-accent-foreground not-data-[variant=destructive]:data-highlighted:**:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    >
      {children}
      <ComboboxPrimitive.ItemIndicator
        render={
          <span className='pointer-events-none absolute right-2 flex size-4 items-center justify-center' />
        }
      >
        <CheckIcon className='pointer-events-none' />
      </ComboboxPrimitive.ItemIndicator>
    </ComboboxPrimitive.Item>
  )
}

function ComboboxGroup({ className, ...props }: ComboboxPrimitive.Group.Props) {
  return <ComboboxPrimitive.Group data-slot='combobox-group' className={cn(className)} {...props} />
}

function ComboboxLabel({ className, ...props }: ComboboxPrimitive.GroupLabel.Props) {
  return (
    <ComboboxPrimitive.GroupLabel
      data-slot='combobox-label'
      className={cn('px-2 py-1.5 text-xs text-muted-foreground', className)}
      {...props}
    />
  )
}

function ComboboxCollection({ ...props }: ComboboxPrimitive.Collection.Props) {
  return <ComboboxPrimitive.Collection data-slot='combobox-collection' {...props} />
}

function ComboboxEmpty({ className, ...props }: ComboboxPrimitive.Empty.Props) {
  return (
    <ComboboxPrimitive.Empty
      data-slot='combobox-empty'
      className={cn(
        'hidden w-full justify-center py-2 text-center text-sm text-muted-foreground group-data-empty/combobox-content:flex',
        className
      )}
      {...props}
    />
  )
}

function ComboboxSeparator({ className, ...props }: ComboboxPrimitive.Separator.Props) {
  return (
    <ComboboxPrimitive.Separator
      data-slot='combobox-separator'
      className={cn('-mx-1 my-1 h-px bg-border', className)}
      {...props}
    />
  )
}

const ComboboxChips = React.forwardRef<
  HTMLDivElement,
  ComboboxPrimitive.Chips.Props & {
    size?: ComboboxSize
    disabled?: boolean
    readOnly?: boolean
  }
>(({ className, size = 'default', disabled = false, readOnly = false, ...props }, ref) => {
  const resolvedSize = resolveComboboxSize(size)
  const isInteractive = !disabled && !readOnly

  return (
    <ComboboxPrimitive.Chips
      ref={ref}
      data-slot='combobox-chips'
      data-size={resolvedSize}
      data-disabled={disabled ? '' : undefined}
      data-readonly={readOnly ? '' : undefined}
      className={cn(
        'group/combobox-chips flex flex-wrap items-center gap-1 rounded-md border border-input bg-transparent bg-clip-padding text-sm transition-[color,border-color] has-aria-invalid:border-destructive has-data-[slot=combobox-chip]:px-1.5 data-[size=default]:min-h-7 data-[size=default]:px-2.5 data-[size=default]:py-0.5 data-[size=lg]:min-h-9 data-[size=lg]:px-3 data-[size=lg]:py-1 dark:bg-input/30 dark:has-aria-invalid:border-destructive/50 data-[readonly]:cursor-default data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
        isInteractive && 'focus-within:border-ring',
        className
      )}
      {...props}
    />
  )
})

function ComboboxChip({
  className,
  children,
  showRemove = true,
  ...props
}: ComboboxPrimitive.Chip.Props & {
  showRemove?: boolean
}) {
  return (
    <ComboboxPrimitive.Chip
      data-slot='combobox-chip'
      className={cn(
        'flex h-[calc(--spacing(5.5))] min-w-0 max-w-full items-center justify-center gap-1.5 rounded-sm bg-muted px-1.5 text-xs font-medium whitespace-nowrap text-foreground has-disabled:pointer-events-none has-disabled:cursor-not-allowed has-disabled:opacity-50 has-data-[slot=combobox-chip-remove]:pr-1 group-data-[size=lg]/combobox-chips:h-6 group-data-[size=lg]/combobox-chips:px-2',
        className
      )}
      {...props}
    >
      <span className='min-w-0 truncate'>{children}</span>
      {showRemove && (
        <ComboboxPrimitive.ChipRemove
          render={<Button variant='ghost' size='icon-xs' />}
          className='shrink-0 opacity-50 hover:opacity-100'
          data-slot='combobox-chip-remove'
        >
          <XIcon className='pointer-events-none' />
        </ComboboxPrimitive.ChipRemove>
      )}
    </ComboboxPrimitive.Chip>
  )
}

function ComboboxChipsInput({
  className,
  disabled = false,
  readOnly = false,
  ...props
}: ComboboxPrimitive.Input.Props) {
  return (
    <ComboboxPrimitive.Input
      disabled={disabled}
      readOnly={readOnly}
      data-slot='combobox-chip-input'
      className={cn(
        'min-w-16 flex-1 bg-transparent outline-none placeholder:text-muted-foreground group-data-[size=lg]/combobox-chips:py-0.5',
        readOnly && !disabled && 'cursor-default caret-transparent',
        disabled && 'cursor-not-allowed',
        className
      )}
      {...props}
    />
  )
}

function useComboboxAnchor() {
  return React.useRef<HTMLDivElement | null>(null)
}

function useComboboxFilteredItems<T>() {
  return ComboboxPrimitive.useFilteredItems<T>()
}

function useComboboxFilter(
  ...args: Parameters<typeof ComboboxPrimitive.useFilter>
): ReturnType<typeof ComboboxPrimitive.useFilter> {
  return ComboboxPrimitive.useFilter(...args)
}

export {
  Combobox,
  ComboboxInput,
  ComboboxContent,
  ComboboxList,
  ComboboxItem,
  ComboboxGroup,
  ComboboxLabel,
  ComboboxCollection,
  ComboboxEmpty,
  ComboboxSeparator,
  ComboboxChips,
  ComboboxChip,
  ComboboxChipsInput,
  ComboboxTrigger,
  ComboboxValue,
  useComboboxFilter,
  useComboboxFilteredItems,
  useComboboxAnchor,
}
